import { Box, Container, Divider, Link, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";

const LAST_UPDATED = "April 24, 2026";

export default function PrivacyPolicyPage() {
  const { t } = useTranslation("pages-account");

  return (
    <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
      <Stack spacing={1} sx={{ mb: 5 }}>
        <Typography
          variant="overline"
          color="secondary.main"
          sx={{ fontWeight: 500, letterSpacing: "0.08em" }}
        >
          {t("PrivacyPolicyPage.eyebrow")}
        </Typography>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 500 }}>
          {t("PrivacyPolicyPage.heading")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("PrivacyPolicyPage.lastUpdated", { date: LAST_UPDATED })}
        </Typography>
      </Stack>

      <Stack spacing={4} sx={{ "& p": { lineHeight: 1.7 } }}>
        <Section>
          <Typography variant="body1">
            <Trans
              i18nKey="PrivacyPolicyPage.intro"
              t={t}
              components={[<strong key="b0" />, <strong key="b1" />, <strong key="b2" />]}
            />
          </Typography>
        </Section>

        <Section title={t("PrivacyPolicyPage.section1.title")}>
          <Typography variant="body1">
            {t("PrivacyPolicyPage.section1.intro")}
          </Typography>
          <Box component="ul" sx={{ pl: 3, "& li": { mb: 1.2, lineHeight: 1.7 } }}>
            <li>
              <Trans i18nKey="PrivacyPolicyPage.section1.account" t={t} components={[<strong key="b0" />]} />
            </li>
            <li>
              <Trans i18nKey="PrivacyPolicyPage.section1.assessment" t={t} components={[<strong key="b0" />]} />
            </li>
            <li>
              <Trans i18nKey="PrivacyPolicyPage.section1.aiPrompts" t={t} components={[<strong key="b0" />]} />
            </li>
            <li>
              <Trans i18nKey="PrivacyPolicyPage.section1.billing" t={t} components={[<strong key="b0" />]} />
            </li>
            <li>
              <Trans i18nKey="PrivacyPolicyPage.section1.usage" t={t} components={[<strong key="b0" />]} />
            </li>
          </Box>
        </Section>

        <Section title={t("PrivacyPolicyPage.section2.title")}>
          <Typography variant="body1">{t("PrivacyPolicyPage.section2.intro")}</Typography>
          <Box component="ul" sx={{ pl: 3, "& li": { mb: 1, lineHeight: 1.7 } }}>
            <li>{t("PrivacyPolicyPage.section2.provide")}</li>
            <li>{t("PrivacyPolicyPage.section2.generate")}</li>
            <li>{t("PrivacyPolicyPage.section2.process")}</li>
            <li>{t("PrivacyPolicyPage.section2.send")}</li>
            <li>{t("PrivacyPolicyPage.section2.monitor")}</li>
            <li>{t("PrivacyPolicyPage.section2.comply")}</li>
          </Box>
          <Typography variant="body1">
            {t("PrivacyPolicyPage.section2.noSell")}
          </Typography>
          <Typography variant="body1">
            {t("PrivacyPolicyPage.section2.lawfulBasis")}
          </Typography>
        </Section>

        <Section title={t("PrivacyPolicyPage.section3.title")}>
          <Typography variant="body1">
            {t("PrivacyPolicyPage.section3.intro")}
          </Typography>
          <Box component="ul" sx={{ pl: 3, "& li": { mb: 1.2, lineHeight: 1.7 } }}>
            <li>
              <Trans i18nKey="PrivacyPolicyPage.section3.anthropic" t={t} components={[<strong key="b0" />]} />
            </li>
            <li>
              <Trans i18nKey="PrivacyPolicyPage.section3.stripe" t={t} components={[<strong key="b0" />]} />
            </li>
            <li>
              <Trans i18nKey="PrivacyPolicyPage.section3.google" t={t} components={[<strong key="b0" />]} />
            </li>
            <li>
              <Trans i18nKey="PrivacyPolicyPage.section3.github" t={t} components={[<strong key="b0" />]} />
            </li>
            <li>
              <Trans i18nKey="PrivacyPolicyPage.section3.railway" t={t} components={[<strong key="b0" />]} />
            </li>
            <li>
              <Trans i18nKey="PrivacyPolicyPage.section3.cloudflare" t={t} components={[<strong key="b0" />]} />
            </li>
            <li>
              <Trans i18nKey="PrivacyPolicyPage.section3.googleFonts" t={t} components={[<strong key="b0" />]} />
            </li>
            <li>
              <Trans i18nKey="PrivacyPolicyPage.section3.posthog" t={t} components={[<strong key="b0" />]} />
            </li>
            <li>
              <Trans i18nKey="PrivacyPolicyPage.section3.sentry" t={t} components={[<strong key="b0" />]} />
            </li>
            <li>
              <Trans i18nKey="PrivacyPolicyPage.section3.formspree" t={t} components={[<strong key="b0" />]} />
            </li>
          </Box>
        </Section>

        <Section title={t("PrivacyPolicyPage.section4.title")}>
          <Typography variant="body1">
            {t("PrivacyPolicyPage.section4.intro")}
          </Typography>
          <Box component="ul" sx={{ pl: 3, "& li": { mb: 1.2, lineHeight: 1.7 } }}>
            <li>
              <Trans i18nKey="PrivacyPolicyPage.section4.essential" t={t} components={[<strong key="b0" />]} />
            </li>
            <li>
              <Trans i18nKey="PrivacyPolicyPage.section4.functional" t={t} components={[<strong key="b0" />]} />
            </li>
            <li>
              <Trans
                i18nKey="PrivacyPolicyPage.section4.analytics"
                t={t}
                components={[
                  <strong key="b0" />,
                  <Link key="b1" href="mailto:support@career-forge.org" />,
                ]}
              />
            </li>
          </Box>
          <Typography variant="body1">
            {t("PrivacyPolicyPage.section4.cloudflareNote")}
          </Typography>
        </Section>

        <Section title={t("PrivacyPolicyPage.section5.title")}>
          <Typography variant="body1">
            {t("PrivacyPolicyPage.section5.body")}
          </Typography>
        </Section>

        <Section title={t("PrivacyPolicyPage.section6.title")}>
          <Typography variant="body1">
            <Trans
              i18nKey="PrivacyPolicyPage.section6.rights"
              t={t}
              components={[<Link key="b0" href="mailto:support@career-forge.org" />]}
            />
          </Typography>
          <Typography variant="body1">
            {t("PrivacyPolicyPage.section6.ccpa")}
          </Typography>
        </Section>

        <Section title={t("PrivacyPolicyPage.section7.title")}>
          <Typography variant="body1">
            <Trans
              i18nKey="PrivacyPolicyPage.section7.safeguards"
              t={t}
              components={[<Link key="b0" href="mailto:support@career-forge.org" />]}
            />
          </Typography>
          <Typography variant="body1">
            {t("PrivacyPolicyPage.section7.breach")}
          </Typography>
        </Section>

        <Section title={t("PrivacyPolicyPage.section8.title")}>
          <Typography variant="body1">
            {t("PrivacyPolicyPage.section8.body")}
          </Typography>
        </Section>

        <Section title={t("PrivacyPolicyPage.section9.title")}>
          <Typography variant="body1">
            {t("PrivacyPolicyPage.section9.body")}
          </Typography>
        </Section>

        <Section title={t("PrivacyPolicyPage.section10.title")}>
          <Typography variant="body1">
            {t("PrivacyPolicyPage.section10.body")}
          </Typography>
        </Section>

        <Section title={t("PrivacyPolicyPage.section11.title")}>
          <Typography variant="body1">
            <Trans
              i18nKey="PrivacyPolicyPage.section11.contact"
              t={t}
              components={[<Link key="b0" href="mailto:support@career-forge.org" />]}
            />
          </Typography>
          <Typography variant="body1">
            <Trans
              i18nKey="PrivacyPolicyPage.section11.seeAlso"
              t={t}
              components={[
                <Link key="b0" component={RouterLink} to="/terms" />,
                <Link key="b1" component={RouterLink} to="/refunds" />,
              ]}
            />
          </Typography>
        </Section>

        <Divider />

        <Typography variant="caption" color="text.secondary">
          {t("PrivacyPolicyPage.disclaimer")}
        </Typography>
      </Stack>
    </Container>
  );
}

function Section({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <Box component="section">
      {title && (
        <Typography variant="h5" component="h2" sx={{ fontWeight: 500, mb: 2 }}>
          {title}
        </Typography>
      )}
      <Stack spacing={2}>{children}</Stack>
    </Box>
  );
}
